/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Flex, Text } from '@tishman/components';
import invariant from 'invariant';
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';

import {
  actions,
  selectCombo,
  selectComboIndex,
  selectFirstTimeSlot,
  selectFlow,
  selectIsCombo,
  selectShopCartDateAsValid,
  selectShopCartDate,
  selectWizardCurrentStep,
  useAppDispatch,
  useAppSelector,
} from '../../store';
import { Step, TimePicker } from '../../components/BuyTickets';
import { useCheckBasketMutation } from '../../services/viva';
import { VivaAPIOperation } from '../../services/viva/constants';
import { Translatable, useTranslations } from '../../utils/use-translations';
import { BuyTicketsLayoutContext } from '../../layouts/BuyTicketsLayout';
import { useCapacityAddon } from '../../utils/use-capacity-addon';

import type { BuyTicketsVariantPageData } from '../../components/BuyTickets';
import type { TicketTypesForFlow } from '../../data/translations/ticketType';

type Props = {
  data: BuyTicketsVariantPageData;
  firstStepNumber: number;
  secondStepNumber: number;
  width?: 'wide' | 'default';
};

const Summary = ({
  stepNumber,
  timeSlotIndex = 0,
}: {
  stepNumber: number;
  timeSlotIndex?: number;
}) => {
  const timeSlot = useAppSelector((state) => state.order.shopCart.timeSlot);
  const currentStep = useAppSelector(selectWizardCurrentStep);
  const timeSlotStart = timeSlot?.[timeSlotIndex]?.startRaw;

  if (currentStep <= stepNumber || !timeSlotStart) return null;

  return (
    <Text>{`${timeSlotStart.hours % 12 || 12}:${timeSlotStart.minutes} ${
      timeSlotStart.meridian
    }`}</Text>
  );
};

export const BuyTicketsComboStepBlock = ({
  data,
  firstStepNumber,
  secondStepNumber,
  width,
}: Props): React.JSX.Element => {
  const { page } = data;
  invariant(page, 'Expected valid buy tickets page json');
  const flow = useAppSelector(selectFlow);
  const {
    getDaysAvailabilityQuery,
    getDayPerformances,
    isLoading: performancesIsLoading,
  } = useContext(BuyTicketsLayoutContext);
  const translations = useTranslations<TicketTypesForFlow>(
    Translatable.TicketType,
  );

  const currentStep = useAppSelector(selectWizardCurrentStep);

  const queryClient = useQueryClient();
  const combo = useAppSelector(selectCombo);
  const isCombo = useAppSelector(selectIsCombo);
  const comboIndex = useAppSelector(selectComboIndex);
  const isValidDate = useAppSelector(selectShopCartDateAsValid);
  const selectedTimeSlot = useAppSelector(selectFirstTimeSlot);
  const shopCartDateTime = useAppSelector(selectShopCartDate);
  const { day: selectedDay } = shopCartDateTime;
  const checkBasketMutation = useCheckBasketMutation(flow);
  const dispatch = useAppDispatch();

  const [stepsTouched, setStepsTouched] = useState([false, false]);

  const stepData = useMemo(() => {
    if (!('comboStep' in page))
      throw new Error('Expected buy tickets page data');
    if (!page.comboStep?.tour?.title)
      throw new Error('Expected buy tickets page tour title');
    if (!page.comboStep?.deck?.title)
      throw new Error('Expected buy tickets page deck title');
    return {
      tourTitle: page.comboStep.tour.title,
      tourDescription:
        translations?.forFlow[flow]?.comboStep?.tour?.description ??
        page.comboStep.tour.description!,
      deckTitle: page.comboStep.deck.title,
      deckDescription:
        translations?.forFlow[flow]?.comboStep?.deck?.description ??
        page.comboStep.deck.description!,
    };
  }, [page, translations]);

  const handlePrepareCheckOut = async () => {
    if (isCombo && comboIndex === 1) {
      dispatch(actions.wizard.setNextStep());
    }
  };

  useEffect(() => {
    if (currentStep === firstStepNumber) {
      dispatch(actions.order.setIsCombo(true));
    }
    if (currentStep <= firstStepNumber) {
      queryClient.invalidateQueries([VivaAPIOperation.GetDayPerformances, 0]);
      dispatch(actions.order.setComboIndex(0));
      // Format the date string with padded month and day
      const dateStr = `${shopCartDateTime.year}-${String(
        shopCartDateTime.month + 1,
      ).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      getDayPerformances?.(dateStr, 0);
    }
    if (currentStep === secondStepNumber) {
      queryClient.invalidateQueries([VivaAPIOperation.GetDayPerformances, 1]);
      dispatch(actions.order.setComboIndex(1));
    }
  }, [currentStep]);

  // Keep track of which steps the user has touched
  useEffect(() => {
    if (currentStep === firstStepNumber && !stepsTouched[0]) {
      setStepsTouched([true, false]);
    }
    if (currentStep === secondStepNumber && !stepsTouched[1]) {
      setStepsTouched([true, true]);
    }
  }, [stepsTouched, currentStep]);

  useCapacityAddon();

  return (
    <Fragment>
      <Step
        data-section="BuyTicketsTimeStep"
        showLoadingModal={
          currentStep === firstStepNumber &&
          (getDaysAvailabilityQuery?.isLoading || performancesIsLoading)
        }
        stepIsValid={selectedTimeSlot !== undefined}
        stepNumber={firstStepNumber}
        summary={
          combo.performances[0] &&
          stepsTouched[0] && <Summary stepNumber={firstStepNumber} />
        }
        title={stepData.tourTitle}
        width={width}
      >
        <Flex
          sx={{
            justifyContent: ['center', 'center', 'flex-end'],
          }}
        >
          <TimePicker
            isLoading={checkBasketMutation.isLoading}
            stepNumber={firstStepNumber}
          />
        </Flex>
      </Step>
      <Step
        data-section="BuyTicketsTimeStep"
        showLoadingModal={
          currentStep === secondStepNumber &&
          (getDaysAvailabilityQuery?.isLoading || performancesIsLoading)
        }
        stepIsValid={
          isValidDate && selectedTimeSlot !== undefined && stepsTouched[0]
        }
        stepNumber={secondStepNumber}
        summary={
          combo.performances[1] &&
          stepsTouched[1] && (
            <Summary stepNumber={secondStepNumber} timeSlotIndex={1} />
          )
        }
        title={stepData.deckTitle}
        width={width}
      >
        <Flex
          sx={{
            justifyContent: ['center', 'center', 'flex-end'],
          }}
        >
          <TimePicker
            description={stepData.deckDescription}
            isLoading={checkBasketMutation.isLoading}
            onContinue={handlePrepareCheckOut}
            stepNumber={secondStepNumber}
          />
        </Flex>
      </Step>
    </Fragment>
  );
};
